import { useEffect, useMemo, useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Checkbox, Dialog, Select, Textarea } from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import type { BusinessAction, BusinessStatus } from "../types";
import { formatLabel } from "../labels";
import { ALLOWED_FROM } from "../lifecycle";

/**
 * Actions that amount to approving the business. `mark_verified` is not offered
 * here (Approve from under_review runs it first), but the warning keys off both
 * so it cannot silently stop appearing if it ever is.
 */
const APPROVING: readonly BusinessAction[] = ["activate", "mark_verified"];

/**
 * Actions that must say why. Mirrors REASON_REQUIRED on the server.
 *
 * Only the ones that leave somebody worse off. Demanding a sentence to say
 * "yes" is what made every Confirm button dead until a reviewer typed
 * something — usually "ok", which teaches them the field is noise.
 */
const REASON_REQUIRED: BusinessAction[] = [
  "request_more_information",
  "reject",
  "restrict",
  "suspend",
  "close",
];

/** A document a reviewer can tick, from the business type's requirement list. */
export interface RequirementDocument {
  key: string;
  label: string;
  required: boolean;
  conditional: boolean;
}

export interface BusinessActionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Drives which actions are offered. */
  status: BusinessStatus;
  /** Documents for this business's type, for the Modify checklist. */
  documents?: RequirementDocument[];
  onSubmit: (input: {
    action: BusinessAction;
    reason?: string;
    approvalId?: string;
    requiredDocuments?: string[];
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
  /**
   * Open preset — used by the documents panel's "Send back for these", which
   * hands over the rejected requirement keys already ticked.
   */
  preset?: { action: BusinessAction; requiredDocuments?: string[] } | null;
  /**
   * Required documents not yet accepted. Approving is not blocked — whether it
   * should be is an open product decision — but a reviewer is told. `null`
   * means the documents could not be checked, which is warned about too.
   */
  outstandingDocuments?: string[] | null;
}

export function BusinessActionDialog({
  open,
  onClose,
  status,
  documents = [],
  onSubmit,
  busy = false,
  error = null,
  preset = null,
  outstandingDocuments = [],
}: BusinessActionDialogProps) {
  const available = useMemo(
    () =>
      (Object.keys(ALLOWED_FROM) as BusinessAction[]).filter((a) =>
        ALLOWED_FROM[a]?.includes(status),
      ),
    [status],
  );

  const [action, setAction] = useState<BusinessAction>(available[0] ?? "close");
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");
  const [checked, setChecked] = useState<string[]>([]);
  /**
   * A preset that cannot run from this status (the business moved on while the
   * page was open). Never quietly swapped for a different action: the reviewer
   * is told, and must pick one themselves before Confirm does anything.
   */
  const [presetRefused, setPresetRefused] = useState<BusinessAction | null>(null);

  // Reopening on a business in a different state must not leave a stale action
  // selected — it would be one that cannot run from here.
  useEffect(() => {
    if (open) {
      const presetAllowed = preset ? available.includes(preset.action) : false;
      setPresetRefused(preset && !presetAllowed ? preset.action : null);
      setAction(presetAllowed && preset ? preset.action : (available[0] ?? "close"));
      setReason("");
      setChecked(presetAllowed && preset ? (preset.requiredDocuments ?? []) : []);
    }
  }, [open, available, preset]);

  const isModify = action === "request_more_information";
  const needsReason = REASON_REQUIRED.includes(action);
  const blocked =
    presetRefused !== null ||
    (needsReason && reason.trim().length < 3) ||
    // Asking for changes without saying which ones is not a request, it is a
    // rejection the applicant cannot act on.
    (isModify && checked.length === 0);

  const toggle = (key: string) =>
    setChecked((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Business status action"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={
              action === "reject" || action === "close" || action === "suspend"
                ? "danger"
                : "primary"
            }
            loading={busy}
            disabled={blocked}
            onClick={() =>
              void onSubmit({
                action,
                // Omitted rather than sent empty: the server reads an absent
                // reason as "none given" and "" as a reason of no characters.
                ...(reason.trim() ? { reason: reason.trim() } : {}),
                ...(approvalId.trim() ? { approvalId: approvalId.trim() } : {}),
                ...(isModify && checked.length > 0
                  ? { requiredDocuments: checked }
                  : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Only actions that are legal from <strong>{formatLabel(status)}</strong>{" "}
          are offered. Refusing, restricting, suspending, cancelling and asking
          for modifications need a reason — the applicant reads it.
        </Text>

        {available.length === 0 ? (
          <Alert tone="warning" title="Nothing to do">
            No status action is available from {formatLabel(status)}.
          </Alert>
        ) : null}

        {presetRefused ? (
          <Alert tone="warning" title={`${formatLabel(presetRefused)} is not possible now`}>
            This business is {formatLabel(status)}, and {formatLabel(presetRefused).toLowerCase()} can
            only be taken from{" "}
            {(ALLOWED_FROM[presetRefused] ?? []).map(formatLabel).join(" or ") || "no status"}.
            Choose an action below if you still want to change it.
          </Alert>
        ) : null}

        <Select
          label="Action"
          value={action}
          onChange={(event) => {
            setPresetRefused(null);
            setAction(event.target.value as BusinessAction);
          }}
          options={available.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />

        {APPROVING.includes(action) && outstandingDocuments === null ? (
          <Alert tone="warning" title="Documents could not be checked">
            Documents could not be checked — approving without reviewing them.
            Reload the Verification documents section to see where each one
            stands before approving.
          </Alert>
        ) : null}

        {APPROVING.includes(action) && outstandingDocuments !== null && outstandingDocuments.length > 0 ? (
          <Alert tone="warning" title="Documents not accepted yet">
            Not accepted: {outstandingDocuments.join(", ")}. Approving anyway is
            allowed — check the Verification documents section first.
          </Alert>
        ) : null}

        {action === "activate" && status === "under_review" ? (
          <Alert tone="info" title="Verifies and activates">
            This business has not been marked verified yet, so approving does
            both steps in order.
          </Alert>
        ) : null}

        {isModify ? (
          <Stack gap={8}>
            <Text role="small" color="secondary" as="p">
              Tick what the applicant must supply again. Only the ticked items
              are shown to them, and only those become editable in the app.
            </Text>
            {documents.length === 0 ? (
              <Alert tone="warning" title="No document list">
                This business type has no requirement list, so there is nothing
                to tick.
              </Alert>
            ) : (
              documents.map((doc) => (
                <Checkbox
                  key={doc.key}
                  name={`req-${doc.key}`}
                  checked={checked.includes(doc.key)}
                  onChange={() => toggle(doc.key)}
                  label={
                    doc.conditional
                      ? `${doc.label} (if it applies)`
                      : doc.required
                        ? doc.label
                        : `${doc.label} (optional)`
                  }
                />
              ))
            )}
          </Stack>
        ) : null}

        {/* Shown only where it means something. An always-present box that is
            usually ignored is how a required field gets filled with "ok". */}
        {needsReason ? (
          <Textarea
            label={isModify ? "What needs changing" : "Reason"}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        ) : null}

        {action === "restrict" || action === "suspend" || action === "restore" ? (
          <ApprovalPickerField
            label="Approved Approval (when required)"
            value={approvalId}
            onChange={setApprovalId}
          />
        ) : null}

        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}
